import Styles from './SelectImage.module.scss'
import { collectResults } from 'utils/query'
import { Button } from 'blaze-ui/Button'
import { Grid } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { useInfiniteBrandKitMediaFiles } from 'hooks/queries/brandKits/useInfiniteBrandKitMediaFiles'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { Checkbox } from 'blaze-ui/Checkbox'
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
} from 'screens/Finder/components/views/GalleryView/constants'
import { useModals } from 'almanac-ui/Modal'
import { SelectedImage } from 'types'
import { AllImages } from 'components/Wizards/DesignWizard/AddPhoto/SelectImage/AllImages'
import { DragOverlayForFirstUpload } from 'components/Design/UploadsTab/DragOverlay'
import { useDropzone } from 'react-dropzone'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCurrentImageLibrary } from 'hooks/useCurrentImageLibrary'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'

interface Props {
  brandKitId: number
  onSelectImage: (value: SelectedImage) => void
  lastSelectedImage?: SelectedImage
  onSelectImageFromAll?: (image: SelectedImage) => void
  isDisabled?: boolean
}

export const BrandKitImages = ({
  brandKitId,
  lastSelectedImage,
  onSelectImage,
  onSelectImageFromAll,
  isDisabled,
}: Props) => {
  const imagePickerContext = useImagePickerContext()
  const { toggleSelectedImage, selectedImages, accept } = imagePickerContext
  const mediaFilesQuery = useInfiniteBrandKitMediaFiles({ brandKitId, items: 4 })
  const mediaFiles = collectResults(mediaFilesQuery, (data) => data.media_files)
  const { openModal, closeModal } = useModals()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const imageLibrary = useCurrentImageLibrary()

  const { uploadImageFiles, isUploading } = useImportMediaFiles(undefined, () => {
    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', brandKitId, 'mediaFiles'])
    if (imageLibrary?.key) {
      queryClient.invalidateQueries(['workspace', workspaceId, 'files', imageLibrary.key])
    }
  })

  const handleDrop = (files: File[]) => uploadImageFiles(files, imageLibrary?.key, { brand_kit_id: brandKitId })

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    accept: accept || ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  })

  const handleOpenAll = () => {
    openModal(
      AllImages,
      {
        ...imagePickerContext,
        accept: accept || ACCEPTED_IMAGE_MIME_TYPES,
        confirmLabel: 'Use Image',
        onPressOutside: closeModal,
        onConfirm: (images) => {
          onSelectImage(images[0])
          closeModal()
          onSelectImageFromAll?.(images[0])
        },
        initialBrandKitId: brandKitId,
      },
      { newStack: true },
    )
  }

  if (mediaFilesQuery.isLoading || mediaFilesQuery.isFetching)
    return (
      <div className={Styles.imagesSection}>
        <Text variant="largeList">or from your image library</Text>
        <div className={Styles.imagesCard} {...getRootProps()}>
          <div className={Styles.imagesGrid}>
            {Array.from({ length: 4 }).map((_, i) => (
              <ShimmeringColor aspectRatio={1} width={152} height={152} key={i} />
            ))}
          </div>
          <Button frontIcon={Grid} variant="tertiary" isDisabled>
            Select from all Brand Kits or Uploads
          </Button>
        </div>
      </div>
    )

  return (
    <div className={Styles.imagesSection}>
      <Text variant="largeList">or from your brand kit</Text>
      <div className={Styles.imagesCard} {...getRootProps()} data-testid="upload-dropzone">
        {mediaFiles.length > 0 && (
          <>
            <div className={Styles.imagesGrid}>
              {mediaFiles.slice(0, 4).map((file) => {
                const isSelected = !!selectedImages.find((selectedImage) => selectedImage.id === file.id)
                return (
                  <Checkbox.Label
                    key={file.id}
                    onChange={() => toggleSelectedImage({ ...file, url: file.source_url })}
                    isSelected={isSelected}
                    aria-label={`Select brand kit image ${file.id}`}
                  >
                    <div
                      className={cx(Styles.image, { [Styles.isSelected]: isSelected, [Styles.isDisabled]: isDisabled })}
                      onClick={() => {}}
                    >
                      <div className={Styles.checkbox}>
                        {isSelected && <Checkbox.Box color="var(--selected-stroke)" />}
                      </div>
                      <img src={file.source_url} alt="uploaded to brand kit" />
                    </div>
                  </Checkbox.Label>
                )
              })}

              {Array.from({ length: 4 - mediaFiles.length }).map((_, i) => (
                <div key={i} className={Styles.emptyPlaceholder} />
              ))}
            </div>
            <Button frontIcon={Grid} onPress={handleOpenAll} variant="tertiary" isDisabled={isDisabled}>
              Select from all Brand Kits or Uploads
            </Button>
          </>
        )}
        {mediaFiles.length === 0 && (
          <>
            <DragOverlayForFirstUpload
              style={{
                minHeight: 314,
                width: 314,
                margin: 0,
                border: '3px dashed var(--dark-15, rgba(0, 0, 0, 0.15))',
                boxShadow: 'none',
              }}
              active={isDragActive}
              withSupportedFilesTag={false}
              buttonProps={{
                children: isUploading ? 'Uploading...' : 'Uploads Files',
                size: 'md',
                variant: 'primary',
                onPress: open,
                isDisabled: isUploading,
              }}
              button={Button}
            />
            <input {...getInputProps()} />
            <Button frontIcon={Grid} onPress={handleOpenAll} variant="tertiary">
              Select from all Brand Kits or Uploads
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
